// Typed API client for the unresolved-deposit refund BFF surface.
//
// Bound to the BE routes under /v2/admin/bff/escrow/refund/* shipped
// in gao-id-worker PR #142. Every call goes through `apiCall` which
// attaches the bearer session token and surfaces 401 as a session-
// expired flag — no HMAC / service secret / private key ever lives
// in this browser bundle.
//
// Response shapes mirror the BE source byte-for-byte; any drift
// surfaces as a TypeScript error at the call site rather than a
// silent JSON mismatch at runtime.

import { apiCall } from "./client";

// ─── Candidates ─────────────────────────────────────────────────────

export type RefundEscrowVersion = "v3" | "v2_deprecated" | "unknown";

export type RefundCandidateStatus = "FAILED" | "EXPIRED" | "CANCELLED";

export interface RefundCandidateView {
  paymentIntentId: string;
  identityId: string;
  domain: string;
  status: RefundCandidateStatus;
  failureReason: string | null;
  failureMessage: string | null;
  depositTxHash: string;
  escrowContract: string;
  escrowVersion: RefundEscrowVersion;
  invoiceId: string;
  token: {
    address: string;
    symbol: string;
    decimals: number;
  };
  amount: {
    baseUnits: string;
    display: string;
  };
  intent: {
    payerWallet: string;
    ownerWallet: string;
  };
  chainId: number;
  billing: {
    status: string;
    sourceService: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface RefundCandidatesListResponse {
  ok: true;
  candidates: RefundCandidateView[];
}

export interface RefundDepositOnchain {
  payer: string;
  buyer: string;
  paymentToken: string;
  grossAmount: string;
  status: number;
  createdAt: string;
  settledAt: string;
  refundedAt: string;
  isPending: boolean | null;
}

export interface RefundSafetyChecks {
  escrowOnTrustedList: boolean;
  onchainIsPending: boolean | null;
  onchainStatusDeposited: boolean | null;
  onchainGrossAmountMatches: boolean | null;
  onchainTokenMatches: boolean | null;
}

export interface RefundCandidateDetailResponse {
  ok: true;
  candidate: RefundCandidateView;
  onchain: RefundDepositOnchain | null;
  refundDestination: {
    address: string;
    /** Always false — the V3 contract refunds to the stored payer.
     *  The FE renders this read-only with a "cannot edit" note. */
    editable: false;
    source: "contract_storage_getDeposit_payer";
  } | null;
  safetyChecks: RefundSafetyChecks;
}

export async function listRefundCandidates(): Promise<RefundCandidatesListResponse> {
  return apiCall<RefundCandidatesListResponse>(
    "/v2/admin/bff/escrow/refund/candidates",
  );
}

export async function getRefundCandidate(
  paymentIntentId: string,
): Promise<RefundCandidateDetailResponse> {
  return apiCall<RefundCandidateDetailResponse>(
    `/v2/admin/bff/escrow/refund/candidates/${encodeURIComponent(
      paymentIntentId,
    )}`,
  );
}

// ─── Simulate ───────────────────────────────────────────────────────

export interface RefundSimulateResponse {
  ok: true;
  paymentIntentId: string;
  invoiceId: string;
  escrowAddress: string;
  escrowVersion: RefundEscrowVersion;
  selector: string;
  calldata: string;
  refundToPayer: string;
  amount: {
    baseUnits: string;
    tokenAddress: string;
  };
  simulation: {
    ok: boolean;
    error: string | null;
    ownerSimulatedFrom: string;
  };
  onchain: {
    isPending: boolean;
    status: number;
  };
}

export async function simulateRefund(
  paymentIntentId: string,
): Promise<RefundSimulateResponse> {
  return apiCall<RefundSimulateResponse>(
    "/v2/admin/bff/escrow/refund/simulate",
    {
      method: "POST",
      body: { paymentIntentId },
    },
  );
}

// ─── Proposal ───────────────────────────────────────────────────────

export interface RefundProposalPayload {
  action: "escrow_refund";
  paymentIntentId: string;
  domain: string;
  invoiceId: string;
  escrowVersion: RefundEscrowVersion;
  escrowAddress: string;
  selector: string;
  calldata: string;
  refundToPayer: string;
  tokenAddress: string;
  amountBaseUnits: string;
  amountMinor: number;
  depositTxHash: string;
  chainId: number;
  simulationOk: true;
}

export interface RefundProposalResponse {
  ok: true;
  proposal: {
    id: string;
    actionType: string;
    status: string;
    approveCount?: number;
    rejectCount?: number;
  };
  actionDigest: string;
  payloadHash: string;
  proposalId: string;
  payload: RefundProposalPayload;
}

export async function createRefundProposal(
  paymentIntentId: string,
): Promise<RefundProposalResponse> {
  return apiCall<RefundProposalResponse>(
    "/v2/admin/bff/escrow/refund/proposals",
    {
      method: "POST",
      body: { paymentIntentId },
    },
  );
}

// ─── Execute (broadcast or calldata-only fallback) ──────────────────

export interface RefundExecuteBroadcastResponse {
  ok: true;
  mode: "broadcast";
  txHash: string;
  blockNumber: number;
  invoiceId: string;
  refundToPayer: string;
  amountBaseUnits: string;
  proposalId: string;
}

export interface RefundExecuteCalldataOnlyResponse {
  ok: false;
  mode: "calldata_only";
  reason: string;
  message: string;
  paymentIntentId: string;
  proposalId: string;
  invoiceId: string;
  escrowAddress: string;
  escrowVersion: RefundEscrowVersion;
  to: string;
  value: "0";
  data: string;
  refundToPayer: string;
  amountBaseUnits: string;
  tokenAddress: string;
  chainId: number;
}

export type RefundExecuteResponse =
  | RefundExecuteBroadcastResponse
  | RefundExecuteCalldataOnlyResponse;

export function isBroadcastResponse(
  r: RefundExecuteResponse,
): r is RefundExecuteBroadcastResponse {
  return r.mode === "broadcast";
}

export function isCalldataOnlyResponse(
  r: RefundExecuteResponse,
): r is RefundExecuteCalldataOnlyResponse {
  return r.mode === "calldata_only";
}

export async function executeRefund(input: {
  paymentIntentId: string;
  proposalId: string;
}): Promise<RefundExecuteResponse> {
  return apiCall<RefundExecuteResponse>("/v2/admin/bff/escrow/refund", {
    method: "POST",
    body: input,
  });
}

// ─── Confirm ────────────────────────────────────────────────────────

export interface RefundConfirmResponse {
  ok: true;
  proposalId: string;
  paymentIntentId: string;
  txHash: string;
  confirmations: number;
  confirmedBlock: number;
  refundedAt: string;
  refundToPayer: string;
  amountBaseUnits: string;
  domainReleased: boolean;
  billingStatus: "REFUNDED";
  paymentIntentStatus: "REFUNDED";
}

export async function confirmRefund(
  proposalId: string,
): Promise<RefundConfirmResponse> {
  return apiCall<RefundConfirmResponse>(
    `/v2/admin/bff/escrow/refund/${encodeURIComponent(proposalId)}/confirm`,
    { method: "POST", body: {} },
  );
}
